import { useState } from 'react';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';

interface MaintenanceRequestFormProps {
  propertyId: string;
  tenantId?: string | null;
  onClose: () => void;
  onSuccess: () => void;
}

export function MaintenanceRequestForm({ propertyId, tenantId, onClose, onSuccess }: MaintenanceRequestFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'plumbing',
    urgency: 'low'
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const { error } = await supabase.from('maintenance_requests').insert({
        property_id: propertyId,
        tenant_id: tenantId || null,
        title: formData.title,
        description: formData.description,
        category: formData.category,
        urgency: formData.urgency,
        status: 'new'
      });

      if (error) throw error;

      toast.success('Maintenance request submitted');
      onSuccess();
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-6 p-6">
      <div className="flex flex-col gap-4">
        <div>
          <label className="block text-sm font-medium text-[var(--t-text-muted)] mb-1">Issue Title</label>
          <input required type="text" value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} placeholder="e.g. Leaking faucet in master bath" className="w-full bg-[var(--t-background)] border border-[var(--t-border)] rounded-xl px-4 py-2 text-white focus:border-[var(--t-primary)] outline-none" />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-[var(--t-text-muted)] mb-1">Category</label>
            <select value={formData.category} onChange={e => setFormData({ ...formData, category: e.target.value })} className="w-full bg-[var(--t-background)] border border-[var(--t-border)] rounded-xl px-4 py-2.5 text-white focus:border-[var(--t-primary)] outline-none capitalize">
              {['plumbing', 'electrical', 'hvac', 'appliance', 'structural', 'other'].map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--t-text-muted)] mb-1">Urgency</label>
            <select value={formData.urgency} onChange={e => setFormData({ ...formData, urgency: e.target.value })} className="w-full bg-[var(--t-background)] border border-[var(--t-border)] rounded-xl px-4 py-2.5 text-white focus:border-[var(--t-primary)] outline-none capitalize">
              {['low', 'medium', 'high', 'emergency'].map(level => (
                <option key={level} value={level}>{level}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-[var(--t-text-muted)] mb-1">Description (Optional)</label>
          <textarea rows={4} value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} placeholder="Provide more details about the issue..." className="w-full bg-[var(--t-background)] border border-[var(--t-border)] rounded-xl px-4 py-3 text-white focus:border-[var(--t-primary)] outline-none resize-none custom-scrollbar" />
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t border-[var(--t-border)]">
        <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-semibold text-[var(--t-text-muted)] hover:text-white transition-colors">Cancel</button>
        <button type="submit" disabled={isSubmitting} className="px-6 py-2 bg-[var(--t-primary)] text-white text-sm font-bold rounded-xl hover:bg-[var(--t-primary-hover)] transition-colors disabled:opacity-50">
          {isSubmitting ? 'Submitting...' : 'Submit Request'}
        </button>
      </div>
    </form>
  );
}
